import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * FuzzySearchInput Component
 * 
 * A reusable autocomplete input that enforces selection from search results.
 * Prevents browser autocomplete conflicts and ensures valid API data.
 * 
 * Features:
 * - Browser autocomplete fully disabled
 * - Dual state management (inputValue vs selectedOption)
 * - Debounced search with loading indicator
 * - Auto-select single match on blur
 * - Inline validation errors
 * - High z-index dropdown to prevent layering issues
 * - Click-outside handler
 * - Initial value restoration for navigation
 * 
 * @example
 * <FuzzySearchInput
 *   id="plan-name"
 *   label="Plan Name"
 *   placeholder="Type to search..."
 *   required
 *   searchFn={(query) => api.searchPlans(query)}
 *   mapResultToOption={(plan) => ({
 *     id: plan.planid.toString(),
 *     label: plan.name,
 *     subtitle: plan.location,
 *     metadata: plan
 *   })}
 *   value={selectedPlan}
 *   onChange={setSelectedPlan}
 *   minSearchLength={1}
 *   debounceMs={300}
 * />
 */
export const FuzzySearchInput = ({
  id,
  label,
  placeholder = 'Type to search...',
  required = false,
  searchFn,
  mapResultToOption,
  value,
  onChange,
  initialValue = null,
  minSearchLength = 1,
  debounceMs = 300,
  disabled = false,
  className = '',
  emptyMessage = 'No matching results found—try different search terms',
  errorMessage = 'Please select a result from the list',
}) => {
  // Dual state: what user types vs what they selected
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  
  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Validation
  const [validationError, setValidationError] = useState('');
  
  // Refs
  const dropdownRef = useRef(null);
  const abortControllerRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const isInitializingRef = useRef(true);
  const isSelectingRef = useRef(false); // Track if user is clicking dropdown option
  
  // Generate random name to prevent browser autofill
  const randomName = useRef(`fuzzy-search-${Math.random().toString(36).substring(2, 15)}`);

  /**
   * Initialize from initialValue prop (for navigation restore)
   * Only runs once on mount
   */
  useEffect(() => {
    if (hasInitializedRef.current) return;
    
    if (initialValue) {
      setSelectedOption(initialValue);
      setInputValue(initialValue.label || '');
      hasInitializedRef.current = true;
      
      // Prevent API calls during initialization
      const timer = setTimeout(() => {
        isInitializingRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    } else {
      hasInitializedRef.current = true;
      isInitializingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync with external value changes
   */
  useEffect(() => {
    if (value === null && selectedOption !== null) {
      // Parent cleared the value, reset local state
      setSelectedOption(null);
      setInputValue('');
    } else if (value !== null) {
      // Parent set a value, sync it to local state
      if (selectedOption?.id !== value.id) {
        setSelectedOption(value);
        setInputValue(value.label || '');
      }
      // We have a valid selection, ensure no errors are showing
      setValidationError('');
    }
  }, [value, selectedOption]);

  /**
   * Clear validation errors when a valid selection exists
   */
  useEffect(() => {
    if (selectedOption) {
      setValidationError('');
    }
  }, [selectedOption]);

  /**
   * Debounced search effect
   */
  useEffect(() => {
    // Skip if initializing
    if (isInitializingRef.current) return;
    
    // Skip if already selected and input matches selection
    if (selectedOption && inputValue === selectedOption.label) return;
    
    // Skip if input too short
    if (!inputValue || inputValue.length < minSearchLength) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const performSearch = async () => {
      setIsSearching(true);
      setValidationError('');

      try {
        const results = await searchFn(inputValue, abortController.signal);
        
        if (!abortController.signal.aborted) {
          // Map results using provided mapper
          let options = [];
          if (Array.isArray(results)) {
            options = results.map(mapResultToOption);
          } else if (results && typeof results === 'object') {
            // Handle response with nested results (e.g., { plans: [...] })
            const resultArray = Object.values(results).find(Array.isArray) || [];
            options = resultArray.map(mapResultToOption);
          }
          
          setSearchResults(options);
          setIsSearching(false);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
          setSearchResults([]);
          setIsSearching(false);
        }
      }
    };

    const debounceTimer = setTimeout(performSearch, debounceMs);

    return () => {
      clearTimeout(debounceTimer);
      abortController.abort();
    };
  }, [inputValue, selectedOption, minSearchLength, debounceMs, searchFn, mapResultToOption]);

  /**
   * Click outside handler
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle input change - typing clears selection
   */
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Prevent scroll jump on mobile
    const scrollY = window.scrollY;
    
    // Clear any validation errors immediately when typing
    setValidationError('');
    
    // If user has a selection and starts typing, clear it and enter edit mode
    if (selectedOption && !isEditing) {
      setIsEditing(true);
      setSelectedOption(null);
      onChange(null);
    }
    
    setInputValue(newValue);
    setShowDropdown(true);
    
    // Restore scroll position
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }, [selectedOption, isEditing, onChange]);

  /**
   * Handle option selection
   */
  const handleSelect = useCallback((option) => {
    // Mark that we're selecting to prevent blur validation
    isSelectingRef.current = true;
    
    setSelectedOption(option);
    setInputValue(option.label);
    setShowDropdown(false);
    setValidationError(''); // Immediately clear any errors
    setIsEditing(false);
    
    // Notify parent
    onChange(option);
    
    // Reset selection flag after a short delay
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 300);
  }, [onChange]);

  /**
   * Handle input focus
   */
  const handleFocus = useCallback(() => {
    // Don't auto-clear selection - let user see what they selected
    // Selection will be cleared when they start typing (in handleInputChange)
    setValidationError('');
    // Don't show dropdown yet - wait for user to start typing
  }, []);

  /**
   * Handle input blur - validate and auto-select if needed
   */
  const handleBlur = useCallback(() => {
    // Delay to allow click events on dropdown to fire
    setTimeout(() => {
      // Don't validate if user is currently selecting from dropdown
      if (isSelectingRef.current) {
        return;
      }
      
      // Don't validate if we already have a valid selection
      if (selectedOption) {
        setValidationError('');
        setShowDropdown(false);
        setIsEditing(false);
        return;
      }
      
      // Only validate if user was actively typing/editing without making a selection
      if (inputValue && inputValue.length >= minSearchLength && isEditing) {
        // User typed but didn't select
        if (searchResults.length === 1) {
          // Auto-select single match
          handleSelect(searchResults[0]);
        } else if (searchResults.length === 0) {
          // No matches found
          setValidationError(emptyMessage);
        } else if (searchResults.length > 1) {
          // Multiple matches, require explicit selection
          setValidationError(errorMessage);
        }
      }
      
      setShowDropdown(false);
      setIsEditing(false);
    }, 250);
  }, [selectedOption, inputValue, minSearchLength, searchResults, isEditing, handleSelect, emptyMessage, errorMessage]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, []);

  // Determine if dropdown should show
  // Show when: dropdown flag is true AND user is actively typing (has inputValue) AND (no selection OR currently editing)
  const shouldShowDropdown = showDropdown && inputValue && (!selectedOption || isEditing);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={id} 
          className="text-sm font-medium text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label} {required && <span className="text-destructive">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative" ref={dropdownRef}>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <Input
            id={id}
            name={randomName.current}
            type="text"
            value={selectedOption ? selectedOption.label : inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className={cn(
              'w-full transition-all duration-200 focus:ring-2 focus:ring-primary/20',
              validationError && 'border-destructive focus:ring-destructive/20'
            )}
            aria-invalid={!!validationError}
            aria-describedby={validationError ? `${id}-error` : undefined}
          />
        </form>

        {/* Loading Indicator */}
        {isSearching && inputValue && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Dropdown Results */}
        {shouldShowDropdown && (
          <Card className="absolute z-[100] w-full mt-2 max-h-72 overflow-auto shadow-xl border-2">
            {isSearching ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {inputValue.length < minSearchLength
                  ? `Type at least ${minSearchLength} character${minSearchLength > 1 ? 's' : ''} to search`
                  : emptyMessage}
              </div>
            ) : (
              <div className="p-1.5">
                {searchResults.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onMouseDown={() => {
                      // Mark selection in progress before blur fires
                      isSelectingRef.current = true;
                    }}
                    onClick={() => handleSelect(option)}
                    className="w-full text-left px-3 py-2.5 rounded-md hover:bg-accent transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <div className="font-medium text-sm text-foreground">
                      {option.label}
                    </div>
                    {option.subtitle && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {option.subtitle}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Validation Error - Absolutely positioned to not affect layout */}
      {validationError && (
        <div 
          id={`${id}-error`}
          className="absolute left-0 top-full mt-1 flex items-center gap-1.5 text-sm text-destructive bg-background px-2 py-1 rounded shadow-sm z-50 border border-destructive/20"
          role="alert"
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{validationError}</span>
        </div>
      )}
    </div>
  );
};

FuzzySearchInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  searchFn: PropTypes.func.isRequired,
  mapResultToOption: PropTypes.func.isRequired,
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    metadata: PropTypes.any,
  }),
  onChange: PropTypes.func.isRequired,
  initialValue: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    metadata: PropTypes.any,
  }),
  minSearchLength: PropTypes.number,
  debounceMs: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  emptyMessage: PropTypes.string,
  errorMessage: PropTypes.string,
};

export default FuzzySearchInput;
